import { useEffect, useRef, useState } from 'react'
import type { Area } from '@/lib/types/area'
import type { TableState, TableCombination } from '@/lib/types/table'
import { DraggableTable } from './DraggableTable'
import { CombinationContainer } from './CombinationContainer'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import type { NearbyInfo } from './FloorPlanCanvas'
import { useUpdateArea } from '@/lib/queries/useAreas'

const areaColors = [
  { bg: 'rgba(232, 168, 56, 0.04)', border: 'rgba(232, 168, 56, 0.2)', text: 'rgba(232, 168, 56, 0.6)' },
  { bg: 'rgba(122, 158, 126, 0.04)', border: 'rgba(122, 158, 126, 0.2)', text: 'rgba(122, 158, 126, 0.6)' },
  { bg: 'rgba(107, 159, 212, 0.04)', border: 'rgba(107, 159, 212, 0.2)', text: 'rgba(107, 159, 212, 0.6)' },
  { bg: 'rgba(155, 127, 232, 0.04)', border: 'rgba(155, 127, 232, 0.2)', text: 'rgba(155, 127, 232, 0.6)' },
  { bg: 'rgba(192, 97, 74, 0.04)', border: 'rgba(192, 97, 74, 0.2)', text: 'rgba(192, 97, 74, 0.6)' },
]

export const AREA_WIDTH = 500
export const AREA_HEIGHT = 420
export const AREA_HEADER = 36

const MIN_AREA_W = 280
const MIN_AREA_H = MIN_AREA_W / 2 + AREA_HEADER

interface AreaContainerProps {
  area: Area
  colorIndex: number
  tableStates: Map<number, { state: TableState; guestName?: string }>
  nearbyGroups: Map<string, NearbyInfo>
  onAreaClick: (areaId: string) => void
  combinations: TableCombination[]
  onBreakCombo?: (documentId: string) => void
  posOrdersByTableDocumentId?: Map<string, 'none' | 'open' | 'in_progress' | 'paid'>
  onPOSTableClick?: (tableDocumentId: string) => void
}

function AreaContainer({ area, colorIndex, tableStates, nearbyGroups, onAreaClick, combinations, onBreakCombo, posOrdersByTableDocumentId, onPOSTableClick }: AreaContainerProps) {
  const color = areaColors[colorIndex % areaColors.length]
  const { setAreaBounds, selectedAreaId, zoom, gridSize, mode } = useFloorPlanStore()
  const updateArea = useUpdateArea()
  const tables = area.tables ?? []
  const isSelected = selectedAreaId === String(area.id)

  const areaM = area.metadata as { width?: number; height?: number } | null
  const [size, setSize] = useState({
    width: areaM?.width ?? AREA_WIDTH,
    height: areaM?.height ?? AREA_HEIGHT,
  })

  // Sync if backend metadata changes (e.g. after save from another session)
  useEffect(() => {
    const m = area.metadata as { width?: number; height?: number } | null
    setSize({ width: m?.width ?? AREA_WIDTH, height: m?.height ?? AREA_HEIGHT })
  }, [area.metadata])

  // Keep store bounds in sync with current size
  useEffect(() => {
    setAreaBounds(String(area.id), { width: size.width, height: size.height - AREA_HEADER })
  }, [area.id, size.width, size.height, setAreaBounds])

  // Group tables into combination containers (first-combo-wins per table)
  const processedIds = new Set<number>()
  const visibleCombos: TableCombination[] = []
  for (const combo of combinations) {
    const ct = combo.tables ?? []
    if (ct.length < 2) continue
    if (!ct.every((t) => tables.some((at) => at.id === t.id))) continue
    if (ct.some((t) => processedIds.has(t.id))) continue
    visibleCombos.push(combo)
    ct.forEach((t) => processedIds.add(t.id))
  }
  const standaloneTables = tables.filter((t) => !processedIds.has(t.id))

  // Resize state — uses pointer capture so no global listeners needed
  const resizeStartRef = useRef({ x: 0, y: 0, w: 0, h: 0 })
  const prevSizeRef = useRef(size)
  const [isResizing, setIsResizing] = useState(false)

  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (mode !== 'layout') return
    e.stopPropagation()
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    prevSizeRef.current = size
    resizeStartRef.current = { x: e.clientX, y: e.clientY, w: size.width, h: size.height }
    setIsResizing(true)
  }

  const handleResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return
    const dx = (e.clientX - resizeStartRef.current.x) / zoom
    const dy = (e.clientY - resizeStartRef.current.y) / zoom
    const snap = (v: number) => Math.round(v / gridSize) * gridSize
    const newW = snap(Math.max(MIN_AREA_W, resizeStartRef.current.w + dx))
    const newH = snap(Math.max(MIN_AREA_H, resizeStartRef.current.h + dy))
    setSize({ width: newW, height: newH })
    setAreaBounds(String(area.id), { width: newW, height: newH - AREA_HEADER })
  }

  const handleResizePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    setIsResizing(false)
    updateArea.mutate(
      { documentId: area.documentId, metadata: { width: size.width, height: size.height } } as any,
      { onError: () => setSize(prevSizeRef.current) },
    )
  }

  const handleAreaClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-table-node]')) return
    if ((e.target as HTMLElement).closest('[data-resize-handle]')) return
    onAreaClick(String(area.id))
  }

  return (
    <div
      className="shrink-0 rounded-[var(--radius-lg)] border transition-all duration-200 relative"
      style={{
        width: size.width,
        height: size.height,
        backgroundColor: color.bg,
        borderColor: isSelected ? color.text : color.border,
        borderWidth: isSelected ? 2 : 1,
        borderStyle: 'dashed',
        overflow: 'hidden',
        cursor: isResizing ? 'se-resize' : undefined,
        userSelect: isResizing ? 'none' : undefined,
      }}
      onClick={handleAreaClick}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 h-9 cursor-pointer"
        style={{ borderBottom: `1px dashed ${color.border}` }}
      >
        <span className="text-xs font-semibold tracking-wide uppercase" style={{ color: color.text }}>
          {area.name}
        </span>
        <span className="text-[10px]" style={{ color: color.border }}>
          {tables.length} tables
        </span>
      </div>

      {/* Table canvas */}
      <div
        className="relative"
        style={{ width: size.width, height: size.height - AREA_HEADER }}
        data-area-id={area.id}
      >
        {standaloneTables.map((table) => {
          const { state, guestName } = tableStates.get(table.id) ?? { state: 'available' as const }
          const combineInfo = nearbyGroups.get(String(table.id))
          return (
            <DraggableTable
              key={table.id}
              table={table}
              state={state}
              guestName={guestName}
              areaId={String(area.id)}
              combineInfo={combineInfo}
              posOrderStatus={posOrdersByTableDocumentId?.get(table.documentId)}
              onPOSTableClick={onPOSTableClick}
            />
          )
        })}
        {visibleCombos.map((combo) => (
          <CombinationContainer
            key={combo.documentId}
            combo={combo}
            areaId={String(area.id)}
            tableStates={tableStates}
            onBreakCombo={onBreakCombo}
            posOrdersByTableDocumentId={posOrdersByTableDocumentId}
            onPOSTableClick={onPOSTableClick}
          />
        ))}
      </div>

      {/* Resize grip — layout mode only */}
      {mode === 'layout' && (
        <div
          data-resize-handle
          style={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            width: 18,
            height: 18,
            cursor: 'se-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <circle cx="8" cy="4" r="1.3" fill={color.text} />
            <circle cx="8" cy="8" r="1.3" fill={color.text} />
            <circle cx="4" cy="8" r="1.3" fill={color.text} />
          </svg>
        </div>
      )}
    </div>
  )
}

interface AreaLayerProps {
  areas: Area[]
  tableStates: Map<number, { state: TableState; guestName?: string }>
  nearbyGroups: Map<string, NearbyInfo>
  onAreaClick: (areaId: string) => void
  combinations: TableCombination[]
  onBreakCombo?: (documentId: string) => void
  posOrdersByTableDocumentId?: Map<string, 'none' | 'open' | 'in_progress' | 'paid'>
  onPOSTableClick?: (tableDocumentId: string) => void
}

export function AreaLayer({ areas, tableStates, nearbyGroups, onAreaClick, combinations, onBreakCombo, posOrdersByTableDocumentId, onPOSTableClick }: AreaLayerProps) {
  return (
    <div className="flex gap-6 p-6 min-w-max">
      {areas.map((area, i) => (
        <AreaContainer
          key={area.id}
          area={area}
          colorIndex={i}
          tableStates={tableStates}
          nearbyGroups={nearbyGroups}
          onAreaClick={onAreaClick}
          combinations={combinations}
          onBreakCombo={onBreakCombo}
          posOrdersByTableDocumentId={posOrdersByTableDocumentId}
          onPOSTableClick={onPOSTableClick}
        />
      ))}
    </div>
  )
}
